import { useEffect, useCallback, useState } from 'react';
import { Outlet, useParams, useOutletContext } from 'react-router-dom';
import {
  PermissionEnum,
  SidePanel,
  SidePanelType,
  ButtonPseudo,
  ChangeLogs,
  PermissionDenied,
  ResponseError,
  getResponseErrors,
} from '@ui/src';
import { useAppSelector } from '@store';
import GeneralInfo from '../../components/bank-and-psp/general-info';
import Documents from '../../components/bank-and-psp/documents';
import {
  useDisablePspPartnerMutation,
  useEnablePspPartnerMutation,
  useLazyGetPspPartnerDocumentsQuery,
  useUpdateVisibleTocClientsMutation,
  useLazyGetBankCASPQuery,
  useLazyGetBankChangeLogsQuery,
} from './store/services';
import { PspPartnerPaymentType } from './types';

import cn from './bank-and-psp.module.css';

const General = () => {
  const {
    bankAndPsp: {
      info: { generalInfo, documents, casp, changeLogs, changeLogsPagination },
    },
    configurationDictionaries: { activePermissions, paymentTypes },
    administrationDictionaries: { users },
  } = useAppSelector((state) => state);
  const { bankId } = useParams();
  const [changeLogsPanelIsOpen, setChangeLogPanelIsOpen] = useState(false);
  const [documentsErrorList, setDocumentsErrorList] = useState('');

  const [disable, { isLoading: disableInProgress }] =
    useDisablePspPartnerMutation();
  const [enable, { isLoading: enableInProgress }] =
    useEnablePspPartnerMutation();

  const [changeVisibility, { isLoading: visiblityChangeInProgress }] =
    useUpdateVisibleTocClientsMutation();

  const { errorList, isLoading, isFetching } = useOutletContext<{
    errorList?: string;
    isLoading: boolean;
    isFetching: boolean;
  }>();

  const [
    getDocuments,
    { isLoading: documentsIsLoading, isFetching: documentsIsFetching },
  ] = useLazyGetPspPartnerDocumentsQuery();
  const [getBankCasp] = useLazyGetBankCASPQuery();
  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetBankChangeLogsQuery();

  const onStatusChange = async (status: boolean) => {
    if (status) {
      await enable(bankId as string);
    } else {
      await disable(bankId as string);
    }
  };

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=PSP_PARTNER&entityId=${bankId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [bankId],
  );

  const changeVisibilityAction = async () => {
    if (bankId) {
      await changeVisibility({
        body: { visibleToClients: !generalInfo?.visibleToClients },
        id: bankId,
      });
    }
  };

  const getPspPartnerDocuments = useCallback(async () => {
    const response = (await getDocuments(bankId as string)) as {
      error?: ResponseError;
    };

    if (response?.error) {
      getResponseErrors({
        error: response.error,
        setErrorList: setDocumentsErrorList,
      });
    }
  }, [bankId]);

  useEffect(() => {
    if (
      !casp &&
      generalInfo &&
      (generalInfo?.paymentsTypes.includes(
        PspPartnerPaymentType.CRYPTO_ASSET_PAYMENT,
      ) ||
        generalInfo?.caspId)
    ) {
      getBankCasp();
    }
  }, [generalInfo, casp]);

  useEffect(() => {
    if (
      bankId &&
      !documents &&
      activePermissions?.includes(PermissionEnum.PSP_DOCUMENTS_LIST)
    ) {
      getPspPartnerDocuments();
    }
  }, [bankId, documents, activePermissions]);

  useEffect(() => {
    let title = generalInfo?.companyName
      ? `Bank: ${generalInfo.companyName}`
      : 'Bank';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [generalInfo]);

  useEffect(() => {
    if (
      changeLogsPanelIsOpen &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getChangeLogsAction();
    }
  }, [changeLogsPanelIsOpen]);

  return (
    <div className={cn.contentWrapper}>
      {errorList && (
        <div className={cn.loadingError}>
          <div className={cn.loadingErrorTitle}>Unable to load data</div>
          <div className={cn.loadingErrorText}>{errorList}</div>
        </div>
      )}
      {!errorList && (
        <>
          <GeneralInfo
            isLoading={isLoading || (!generalInfo && isFetching)}
            generalInfo={generalInfo}
            onStatusChange={onStatusChange}
            statusChangeInProgress={disableInProgress || enableInProgress}
            bankId={bankId}
            changeVisibility={changeVisibilityAction}
            isEditable={activePermissions?.includes(
              PermissionEnum.PSP_EDIT_ACTION,
            )}
            setChangeLogPanelIsOpen={setChangeLogPanelIsOpen}
            deactivateAllowed={activePermissions?.includes(
              PermissionEnum.PSP_DELETE_ACTION,
            )}
            auditLogAvailable={activePermissions?.includes(
              PermissionEnum.AUDIT_LOG_LIST,
            )}
            visiblityChangeInProgress={visiblityChangeInProgress}
            paymentTypes={paymentTypes}
          />

          {activePermissions?.includes(PermissionEnum.PSP_DOCUMENTS_LIST) && (
            <Documents
              isLoading={documentsIsLoading || documentsIsFetching}
              documents={documents}
              bankId={bankId}
              documentsErrorList={documentsErrorList}
              isEditable={activePermissions?.includes(
                PermissionEnum.PSP_DOCUMENTS_CREATE_ACTION,
              )}
            />
          )}
        </>
      )}

      <SidePanel
        isOpen={changeLogsPanelIsOpen}
        onClose={() => {
          setChangeLogPanelIsOpen(false);
        }}
        title="Bank change logs"
        type={SidePanelType.Accounts}
        sidepanelExpandedKey="bank-change-logs"
        footer={
          <div>
            <ButtonPseudo
              text="Close"
              onClick={() => setChangeLogPanelIsOpen(false)}
              bold
            />
          </div>
        }
        closeOnClickOutside
      >
        <>
          {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
            <ChangeLogs
              entityType="PSP_PARTNER"
              entityId={bankId as string}
              changeLogsIsLoading={changeLogsIsLoading}
              changeLogs={changeLogs}
              activePermission={activePermissions}
              users={users}
              pagination={changeLogsPagination}
              changeLogsIsFetching={changeLogsIsFetching}
              getChangeLogs={getChangeLogsAction}
            />
          )}
          {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
            <PermissionDenied />
          )}
        </>
      </SidePanel>
      <Outlet />
    </div>
  );
};

export default General;
